import * as React from "react";
import {cx} from "emotion";

import "./dates-section.scss";

import {cs}               from "@Common/react/chain-services";
import {UseState}         from "@Common/react/use-state";
import {Static2}          from "@Common/react/static-2";
import {
    addDate,
    addMonth,
    today,
    le
}                         from "@Common/utils/date-object";
import {parseDateFormat}  from "@Common/utils/date/parse-date-format";
import {DatePicker2}      from "@Common/ui-components/date-picker-2/date-picker-2";
import {ErrorHighlighter} from "@Common/ui-components/light/error-highlighter";

export const DatesSection = ({next}) => cs(
    ["dates", ({}, next) => UseState({
        initValue: {
            dateRange: 30,
            dates: {
                startDate: getOffsetDateDays(-30),
                endDate: today()
            }
        },
        next,
    })],
    ["eh", (_, next) => ErrorHighlighter({next})],
    ["domRef", (_, next) => Static2({next})],
    ({dates, eh, domRef}) => next({
        valid: (
            dates.value.dates.startDate &&
            dates.value.dates.endDate &&
            le(dates.value.dates.startDate, dates.value.dates.endDate) &&
            le(getOffsetDateMonths(-6), dates.value.dates.startDate) &&
            le(dates.value.dates.endDate, today())
        ),
        highlightErrors: () => {
            eh.start();
            domRef.get().scrollIntoView({behavior: "smooth", block: 'end'});
        },
        dates: dates.value.dates,
        render: ({resultsRendered, showErrors}) => (
            <div className="dates-section-4k5">
                <div className="header">
                    <div className="title" xd-qa-type="label" xd-qa-name="New Homeowners Home Purchase Date">
                        Home Purchase Date
                    </div>
                    
                    <div className="sub-title" xd-qa-type="label" xd-qa-name="New Homeowners Home Purchase Date Description">
                        Define the range of dates a property was purchased between. Dates selected must be within the last 6 months.
                    </div>
                </div>
                
                <div className="default-dates">
                    <div className="label" xd-qa-type="label" xd-qa-name="New Homeowners Last">
                        Last:
                    </div>
                    
                    {[30, 60, 90].map((days) => (
                        <div 
                            key={days}
                            className={cx("date-range", {selected: dates.value.dateRange === days})}
                            xd-qa-type="button"
                            xd-qa-name={`New Homeowners ${days} Days`}
                            onClick={() => dates.onChange({
                                dateRange: days,
                                dates: {
                                    startDate: getOffsetDateDays(-days),
                                    endDate: today(),
                                }
                            })}
                        >
                            {days} Days
                        </div>
                    ))}
                    <div className="spacer"/>
                    <div 
                        className={cx("date-range custom", {selected: dates.value.dateRange === "custom"})}
                        xd-qa-type="button"
                        xd-qa-name="New Homeowners Custom Range"
                        onClick={() => dates.onChange({
                            ...dates.value,
                            dateRange: "custom",
                        })}
                    >
                        Custom Range
                    </div>
                </div>

                <div className={cx("box", {lowest: !resultsRendered}, eh.css)} ref={domRef.set}>
                    {(() => {
                        const df = parseDateFormat("mm/dd/yy");
                        const maxDate = today();
                        const minDate = getOffsetDateMonths(-6);
                        const minError = "Date must be within the last 6 months: after " + df.formatDate(addDate(minDate, -1));
                        const maxError = "Date must be within the last 6 months: before " + df.formatDate(addDate(maxDate, 1));
                        const hasEarlyError = (
                            !dates.value.dates.startDate || 
                            (!le(minDate, dates.value.dates.startDate) && minError) ||
                            (!le(dates.value.dates.startDate, maxDate) && maxError) || (
                                dates.value.dates.endDate &&
                                !le(dates.value.dates.startDate, dates.value.dates.endDate)
                            )
                        );
                        const hasLateError = (
                            !dates.value.dates.endDate || 
                            (!le(minDate, dates.value.dates.endDate) && minError) ||
                            (!le(dates.value.dates.endDate, maxDate) && maxError) || (
                                dates.value.dates.startDate &&
                                !le(dates.value.dates.startDate, dates.value.dates.endDate)
                            )
                        );
                        const earlyError = (showErrors.value.includes("all") || showErrors.value.includes("dates") ) && hasEarlyError;
                        const lateError = (showErrors.value.includes("all") || showErrors.value.includes("dates") ) && hasLateError;
                        
                        return <>
                            <div className="date">
                                <div className={cx("label", {error: !!earlyError})}>
                                    Earliest Purchase Date
                                </div>
                                {DatePicker2({
                                    className: "date-picker",
                                    hasError: !!earlyError,
                                    state: {
                                        value: dates.value.dates.startDate,
                                        onChange: (date) => dates.change((_) => ({dateRange: "custom", dates: {..._.dates, startDate: date}})),
                                    },
                                    minDate,
                                    maxDate,
                                    limitMonthDropdown: true,
                                    placeholder: "MM/DD/YY",
                                    dateFormat: df,
                                    xdQaName: "New Homeowners Earliest Purchase Date",
                                    onBlur: () => {
                                        if (!showErrors.value.includes("dates") && (!!hasEarlyError || !!hasLateError)) {
                                            showErrors.onChange([...showErrors.value, "dates"])
                                        }
                                    },
                                })}
                                {typeof earlyError === 'string' && (
                                    <div className="errorMsg">{earlyError}</div>
                                )}
                            </div>
                            <div className="to"/>
                            <div className="date">
                                <div className={cx("label", {error: !!lateError})}>
                                    Latest Purchase Date
                                </div>
                                {DatePicker2({
                                    className: "date-picker",
                                    hasError: !!lateError,
                                    state: {
                                        value: dates.value.dates.endDate,
                                        onChange: (date) => dates.change((_) => ({dateRange: "custom", dates: {..._.dates, endDate: date}})),
                                    },
                                    minDate,
                                    maxDate,
                                    limitMonthDropdown: true,
                                    placeholder: "MM/DD/YY",
                                    dateFormat: df,
                                    xdQaName: "New Homeowners Latest Purchase Date",
                                    onBlur: () => {
                                        if (!showErrors.value.includes("dates") && (!!hasEarlyError || !!hasLateError)) {
                                            showErrors.onChange([...showErrors.value, "dates"])
                                        }
                                    },
                                })}
                                {typeof lateError === 'string' && (
                                    <div className="errorMsg">{lateError}</div>
                                )}
                            </div>
                        </>
                    })()}
                </div>
            </div>
        ),
    }),
);

const getOffsetDateDays = (offset) => {
    const date = today();
    const dateOffset = addDate(date, offset);
    return dateOffset;
}

const getOffsetDateMonths = (offset) => {
    const date = today();
    const dateOffset = addMonth(date, offset);
    return dateOffset;
}